import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { NGXLogger } from "ngx-logger";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "src/environments/environment";
import {
  ApiUsageAnalyticsParams,
  ApiUsageCategoryStatsResponse,
  ApiUsageModelStatsResponse,
  ApiResponse,
  ApiUsageReportResponse,
  ApiUsageTimeseriesParams,
  ApiUsageTimeseriesResponse,
  LastApiRequests,
} from "../api-usage-reports/model";
import {
  RecompareExecution,
  RecompareExecutionParams,
  RecompareReport,
  RecompareRequest,
} from "../categorize-recompare/model";
import {
  CategoryPunchBulkUpdate,
  CategoryPunchRefreshRequest,
  CategoryPunchResponse,
  CategoryPunchUpdate,
} from "../category-builder/category-punch/category-punch.model";
import {
  CategorizeSumupResponse,
  CategoryHashes,
  CategoryModelVersionApplication,
  DeltaStats,
  Expression,
  FilterFacetCounts,
  OperandType,
  PaginatedHits,
  Recommendations,
  SentencesResults,
  SortDirection,
  TransientData,
} from "../category-builder/category-builder.models";
import { insightsChartParams } from "../charts/charts.model";
import { ChatSegment, ChatSessionTranscript } from "../chats/model";
import {
  Chat,
  ChatSession,
  ChatSummary,
  SelectedVersions,
} from "../chats/state/chat.model";
import { Table } from "../data-source-import/file-parsing/model";
import {
  AvailableDimensions,
  DataSource,
  DataSourceTypes,
} from "../data-source/state/data-source.model";
import { ExportFile } from "../downloads/model";
import { MLModelConfig } from "../emperor-penguin/emperor-penguin.models";
import { Job, PipeNames } from "../job/state/job.model";
import {
  CommonModelType,
  Model,
  ModelReleaseHistory,
  ModelRequest,
  ModelsApplied,
  ModelVersion,
  ModelVersionData,
  ModelVersionOptions,
  SentimentDetails,
} from "../models/state/model";
import { PersistentNotification } from "../persistent-notification/state/persistent-notification.model";
import {
  TokenFrequencyWithPos,
  DetailedTokenFrequencyWithPos,
} from "../shared/models/models.model";
import { Survey, SurveySummary } from "../surveys/state/survey.model";
import {
  Workspace,
  WorkspaceDetails,
} from "../workspace/state/workspaces.model";

export interface PercolatorMigrationResponse {
  [key: string]: unknown;
}

export interface PercolatorStatsResponse {
  category_version_id: string;
  data_source_id: string;
  index_exists: boolean;
  index_name?: string;
  total_rules: number;
  matching_model_version_rules: number;
  rules_out_of_date: boolean;
  ready_for_categorize_v2: boolean;
}

export function percolatorErrorMessage(error: any, versionID: string): string {
  if (error?.status === 409) {
    // Reaching this means an automatic migration already ran and the rules are
    // still not usable, so there is no "migrate first" step left to suggest.
    return `Rules could not be migrated for version ${versionID}. Check the server logs.`;
  }
  if (error?.status === 408) {
    const elapsed = error?.error?.timings?.request_ms;
    const elapsedText = Number.isFinite(elapsed)
      ? ` (${elapsed >= 1000 ? `${(elapsed / 1000).toFixed(2)}s` : `${Math.round(elapsed)}ms`} elapsed)`
      : "";
    return `The request timed out waiting for a worker${elapsedText}.`;
  }
  return (
    error?.error?.details?.description ||
    error?.error?.result_set?.error?.details?.description ||
    error?.error?.detail ||
    error?.statusText ||
    error?.message ||
    // Nothing in the response said anything. The status code is then the only
    // fact available, and "failed" on its own is not actionable.
    (error?.status
      ? `Percolator request failed (HTTP ${error.status}).`
      : "Percolator request failed.")
  );
}

export interface PercolatorCategorizeRequest {
  workspace_id: string;
  category_version_id: string;
  texts: string[];
  max_matches_per_text: number;
}

export interface PercolatorCategorizeResponse {
  [key: string]: unknown;
}

export interface CategorizeApplyReport {
  data_source_id: string;
  category_version_id: string;
  engine: string;
  /** Baked into the compiled queries and the category hashes, so two engines
   * that ran with different values are not comparable. */
  sentiment_version_id?: string;
  job_uuid?: string;
  total_elapsed_ms: number;
  stages: { [stage: string]: number };
  total_documents: number;
  categorized_documents: number;
  categories_in_model: number;
  categories_applied: number;
  total_matches: number;
  throughput_docs_per_sec: number;
  applied_at?: string;
  category_sentence_hits?: { [categoryId: string]: number };
  category_verbatim_hits?: { [categoryId: string]: number };
  category_labels?: { [categoryId: string]: string };
}

export interface CategorizeApplyReportComparison {
  data_source_id: string;
  category_version_id: string;
  legacy?: CategorizeApplyReport | null;
  percolator?: CategorizeApplyReport | null;
}

export interface PercolatorDeltaExamples {
  category_id: string;
  current_engine?: string | null;
  percolator_only: SentencesResults;
  legacy_only: SentencesResults;
}

// Percolator production pilot activation window: a continuous datetime range
// (may span several days). Timestamps are ISO datetimes with timezone.
export interface PercolatorPilotScheduleCreate {
  source_model_id: string;
  mirror_workspace_id: string;
  mirror_model_id: string;
  start_at: string;
  end_at: string;
}

export interface PercolatorPilotSchedule extends PercolatorPilotScheduleCreate {
  uuid: string;
  active: boolean;
  created_at: string;
}

// A released category model of the mirror workspace, selectable as the pilot
// target. The version number is informative: the fork always runs against the
// version released when a request arrives.
export interface PercolatorPilotMirrorModel {
  uuid: string;
  label: string;
  released_version_id: string;
  released_version_number: number;
}

@Injectable({ providedIn: "root" })
export class EaredDoveService {
  constructor(
    private logger: NGXLogger,
    private http: HttpClient,
  ) {}

  createWorkspace(workspace: Workspace) {
    this.logger.info(`Create workspace in Eared Dove`, workspace);
    const url = `${environment.api_url_eared_dove}/workspace`;
    return this.http.post<Workspace>(url, workspace);
  }

  deleteWorkspace(workspace: Workspace) {
    this.logger.info(`Delete workspace from Eared Dove`, workspace.label);
    const url = `${environment.api_url_eared_dove}/workspace`;
    return this.http.delete(url, {
      params: { workspace: workspace.uuid, name: workspace.name },
    });
  }

  getBoSpecificWorkspaces() {
    const url = `${environment.api_url_eared_dove}/workspace`;
    return this.http.get<Workspace[]>(url);
  }

  getWorkspaceDetails() {
    this.logger.info(`Get workspace details from Eared Dove`);
    const url = `${environment.api_url_eared_dove}/workspace/details`;
    return this.http.get<WorkspaceDetails[]>(url);
  }

  getSurveys() {
    this.logger.info(`Get surveys for workspace from Eared Dove`);
    const url = `${environment.api_url_eared_dove}/data-source/survey`;
    return this.http.get<SurveySummary[]>(url);
  }

  deleteSurvey(survey: SurveySummary) {
    this.logger.info(`Delete survey from Eared Dove`);
    const url = `${environment.api_url_eared_dove}/data-source/survey/${survey.uuid}`;
    return this.http.delete(url);
  }

  //TODO: WE CAN GET RID OF THE surveyId PARAMETER ONCE WE DELETE SURVEY-IMPORT-LARGE COMPONENT
  createSurvey(
    label: string,
    structuredDataAttributes,
    surveyId: string = undefined,
  ) {
    this.logger.info(`Creating index ${label} for workspace from Eared Dove`);

    const url = `${environment.api_url_eared_dove}/data-source/survey`;
    const params = { survey_label: label };
    if (surveyId) {
      params["survey_id"] = surveyId;
    }
    return this.http.post<Survey>(url, structuredDataAttributes, {
      params,
    });
  }

  createLargeSurvey(
    label: string,
    folderName: string,
    table: Table,
    mlConfig: MLModelConfig,
    translate: boolean,
  ) {
    this.logger.info(`Starting large import from ${folderName}`);

    const url = `${environment.api_url_eared_dove}/data-source/large-survey`;
    const config = {
      structured_mappings: table.surveyStructuredAttributesMappings,
      survey_label: label,
      lake_folder_name: folderName,

      // TODO: reuse createSurveyImportJob in emperor-penguin-service
      // ML stages (Raven + Mockingbird) have been retired; sentiment is lexicon (Flamingo) only.
      pipeline: [
        PipeNames.TRANSLATE,
        PipeNames.TEXT_ANALYTICS,
        PipeNames.FLAMINGO,
        PipeNames.PERSISTENCE_SURVEY,
        PipeNames.CATEGORIZE_AND_PERSIST,
        PipeNames.DATA_SOURCE_FULLY_IMPORTED,
      ],
      configuration: {
        flamingo_process: "sentiment",
        import_def_uuid: "deadbeef-abed-d00d-babe-beddeddecade",
        analyze_attributes: table.analysisAttributes,
        ml_config: mlConfig,
        text_process: "nlp-spacy",
        do_translate: false,
        applyCategory: !!mlConfig.category_version_id,
      },
    };

    if (!translate) {
      config.pipeline = config.pipeline.filter(
        (pipe) => pipe != PipeNames.TRANSLATE,
      );
    }

    if (!config.configuration.applyCategory) {
      config.pipeline = config.pipeline.filter(
        (pipe) => pipe != PipeNames.CATEGORIZE_AND_PERSIST,
      );
    }

    this.logger.info(`Large import ${folderName} ready to post to ${url}`);
    return this.http.post<any>(url, config);
  }

  getSurveyStructuredAttributesMappings(table: Table) {
    const structuredDataAttributes = table.columns
      .filter((c) => table.analyzed !== c.id && c.import)
      .map((c) => ({
        name: c.name,
        str_type: c.type === "text" ? "keyword" : c.type,
      }));

    return structuredDataAttributes;
  }

  updateSurveyLabel(survey: Survey, newLabel: string) {
    this.logger.info(`Update survey label "${survey.label}" -> "${newLabel}"`);

    const url = `${environment.api_url_eared_dove}/data-source/survey/${survey.uuid}`;
    return this.http.put(url, { label: newLabel });
  }

  getModels() {
    this.logger.info(`Get Sentiment Models for workspace from Eared Dove`);
    const url = `${environment.api_url_eared_dove}/model`;
    return this.http.get<Model[]>(url);
  }

  createNewModel(model: ModelRequest): Observable<Model> {
    this.logger.info(
      `Creating new model ${model.model_label} for workspace from Eared Dove`,
    );
    const url = `${environment.api_url_eared_dove}/model`;
    const params = new HttpParams()
      .set("model_label", model.model_label)
      .set("description", model.description)
      .set("model_type", model.model_type)
      .set("workspace", model.workspace);

    return this.http.post<Model>(url, null, { params });
  }

  updateModelLabel(model: Model, newLabel: string) {
    this.logger.info(`Update model label "${model.label}" -> "${newLabel}"`);
    const url = `${environment.api_url_eared_dove}/model/${model.uuid}`;
    return this.http.put(url, { label: newLabel });
  }

  updateModelMessage(uuid: string, message: string) {
    this.logger.info(`Update model version message -> "${message}"`);
    const url = `${environment.api_url_eared_dove}/model-version/${uuid}`;
    return this.http.put(url, { message });
  }

  updateModelVersionData(
    uuid: string,
    version_data: any,
    updated: Date = new Date(),
  ) {
    this.logger.info(`Update model version data -> "${version_data}"`);
    const url = `${environment.api_url_eared_dove}/model-version/${uuid}`;
    return this.http.put<ModelVersion>(url, {
      version_data,
      updated: updated.toISOString(),
    });
  }

  updateReleaseContext(
    uuid: string,
    releaseContext: any,
    updated: Date = new Date(),
  ) {
    this.logger.info(`Update model release context -> "${releaseContext}"`);
    const url = `${environment.api_url_eared_dove}/model-version/${uuid}`;
    return this.http.put<ModelVersion>(url, {
      release_context: releaseContext,
      updated: updated.toISOString(),
    });
  }

  makeObsolete(model: Model) {
    this.logger.info(`Archive model`);
    const url = `${environment.api_url_eared_dove}/model/${model.uuid}`;
    return this.http.put(url, { obsolete: true });
  }

  makeUnObsolete(model: Model) {
    this.logger.info(`Restore model`);
    const url = `${environment.api_url_eared_dove}/model/${model.uuid}`;
    return this.http.put(url, { obsolete: false });
  }

  deleteModel(model: Model) {
    this.logger.info(`Make model obsolete`);
    const url = `${environment.api_url_eared_dove}/model/${model.uuid}`;
    const params = new HttpParams().set("delete", "true");
    return this.http.delete(url, { params });
  }

  getModelVersion(versionID: string) {
    this.logger.info(`Getting model version info`);
    const url = `${environment.api_url_eared_dove}/model-version/${versionID}`;
    return this.http.get<ModelVersion>(url);
  }

  getModelVersionData(versionID: string) {
    const url = `${environment.api_url_eared_dove}/model-version/${versionID}/version-data`;
    return this.http.get<ModelVersionData>(url);
  }

  getModelVersions(modelID: string) {
    this.logger.info(`Getting model versions`);
    const params = new HttpParams().set("model", modelID);
    const url = `${environment.api_url_eared_dove}/model-version`;
    return this.http.get<ModelVersion[]>(url, { params });
  }

  getAllModelVersions() {
    const url = `${environment.api_url_eared_dove}/model-version/all`;
    return this.http.get<ModelVersion[]>(url);
  }

  deleteModelVersion(modelVersion) {
    this.logger.info(`Deleting model version`);
    const url = `${environment.api_url_eared_dove}/model-version/${modelVersion.uuid}`;
    const params = new HttpParams().set("delete", "true");
    return this.http.delete(url, { params });
  }

  createModelVersion(
    modelID: string,
    message: string,
    copyVersion: string = null,
    versionData: ModelVersionData = null,
  ) {
    this.logger.info("Cloning model");

    let params = new HttpParams().set("model", modelID).set("message", message);
    if (copyVersion) {
      params = params.set("copy_version", copyVersion);
    }

    const url = `${environment.api_url_eared_dove}/model-version`;
    return this.http.post<ModelVersion>(url, versionData, { params });
  }

  getModelReleaseHistory(modelID: string) {
    this.logger.info(`Getting model release history`);
    const url = `${environment.api_url_eared_dove}/model/${modelID}/release-history`;
    return this.http.get<ModelReleaseHistory[]>(url);
  }

  getCategoryPunches() {
    this.logger.info(`Get category punches for workspace from Eared Dove`);
    const url = `${environment.api_url_eared_dove}/category-punch`;
    return this.http.get<CategoryPunchResponse>(url);
  }

  upsertCategoryPunch(update: CategoryPunchUpdate) {
    this.logger.info(`Upsert category punch "${update.category}"`);
    const url = `${environment.api_url_eared_dove}/category-punch`;
    return this.http.put<CategoryPunchResponse>(url, update);
  }

  bulkUpsertCategoryPunches(bulkUpdate: CategoryPunchBulkUpdate) {
    this.logger.info(
      `Bulk upsert ${bulkUpdate.updates.length} category punches`,
    );
    const url = `${environment.api_url_eared_dove}/category-punch`;
    return this.http.patch<CategoryPunchResponse>(url, bulkUpdate);
  }

  refreshCategoryPunches(refreshRequest: CategoryPunchRefreshRequest) {
    this.logger.info(
      `Refresh category punches from version ${refreshRequest.category_version_id}`,
    );
    const url = `${environment.api_url_eared_dove}/category-punch/refresh`;
    return this.http.post<CategoryPunchResponse>(url, refreshRequest);
  }

  getChats() {
    this.logger.info(`Get chats for workspace from Eared Dove`);
    const url = `${environment.api_url_eared_dove}/data-source/chat/summary-metrics`;
    return this.http.post<ChatSummary[]>(url, []);
  }

  deleteChat(chat: ChatSummary) {
    this.logger.info(`Delete chat source from Eared Dove`);
    const url = `${environment.api_url_eared_dove}/data-source/chat/${chat.uuid}`;
    return this.http.delete(url);
  }

  createChat(
    label: string,
    structuredDataAttributes,
    fileUpload: string,
    sessionIdField: string,
    isMessagePerRow: boolean,
  ) {
    this.logger.info(`Creating index ${label} for workspace from Eared Dove`);

    const url = `${environment.api_url_eared_dove}/data-source/chat`;
    return this.http.post<Chat>(url, structuredDataAttributes, {
      params: {
        chat_label: label,
        file_upload: fileUpload,
        session_id_field: sessionIdField,
        is_message_per_row: isMessagePerRow ? "true" : "false",
      },
    });
  }

  updateChatLabel(chat: Chat, newLabel: string) {
    this.logger.info(
      `Update chat source label "${chat.label}" -> "${newLabel}"`,
    );

    const url = `${environment.api_url_eared_dove}/data-source/chat/${chat.uuid}`;
    return this.http.put(url, { label: newLabel });
  }

  importLexicon(fullLexicon: any) {
    this.logger.info("import lexicon");
    const url = `${environment.api_url_eared_dove}/sentiment-lexicon/import-sentiment-lexicon`;
    return this.http.post(url, fullLexicon);
  }

  releaseModel(modelID: string, version: string) {
    this.logger.info("Releasing model");

    const params = new HttpParams()
      .set("model", modelID)
      .set("version", version);
    const url = `${environment.api_url_eared_dove}/model/${modelID}/release/${version}`;
    return this.http.put(url, {}, { params });
  }

  updateModelDesc(model: Model, newDesc: string) {
    this.logger.info(
      `Update model description "${model.label}" -> "${newDesc}"`,
    );
    const url = `${environment.api_url_eared_dove}/model/${model.uuid}`;
    return this.http.put(url, { description: newDesc });
  }

  getChatSessions(chatId: string, models: SelectedVersions[] = []) {
    this.logger.info(`Getting chat sessions`);
    const params = new HttpParams().set("chat", chatId);
    const url = `${environment.api_url_eared_dove}/data-source/chat/${chatId}/sessions/summary-metrics`;
    return this.http.post<ChatSession[]>(url, models, { params });
  }

  getChatSession(
    chatID: string,
    sessionID: string,
    models: SelectedVersions[] = [],
  ) {
    this.logger.info(`Get a chat session for chat from Eared Dove`);
    const url = `${environment.api_url_eared_dove}/data-source/chat/${chatID}/sessions/${sessionID}/summary-metrics`;
    const params = new HttpParams()
      .set("session", sessionID)
      .set("chat", chatID);

    return this.http.post<ChatSession>(url, models, { params });
  }

  deleteSession(chatId: string, sessionId: string) {
    this.logger.info("Deleting chat session");

    const url = `${environment.api_url_eared_dove}/data-source/chat/${chatId}/sessions/${sessionId}`;
    return this.http.delete(url).pipe(map(() => true));
  }

  getChatTranscriptBySession(chatId: string, cxs_session_uuid: string) {
    this.logger.info(
      `Getting chat transcript : ${chatId} and session ${cxs_session_uuid}`,
    );
    const params = new HttpParams().set("chat", chatId);
    const url = `${environment.api_url_eared_dove}/data-source/chat/transcript/${cxs_session_uuid}`;
    return this.http.get<ChatSessionTranscript>(url, { params });
  }

  downloadChatExport(chatId: string) {
    this.logger.info(`Downloading chat export : ${chatId}`);
    const url = `${environment.api_url_eared_dove}/data-source/chat/${chatId}/export`;
    return this.http.get(url, {
      reportProgress: true,
      observe: "events",
      responseType: "blob",
    });
  }

  getAppliedModels(dataSourceId: string) {
    this.logger.info(`Getting applied models: ${dataSourceId}`);
    const params = new HttpParams().set("data_source", dataSourceId);
    const url = `${environment.api_url_eared_dove}/model/models-applied/${dataSourceId}`;
    const models = this.http.get<ModelsApplied>(url, { params });
    return models;
  }

  getChatsById(chatId: string, models: SelectedVersions[] = []) {
    this.logger.info(`Get chats for workspace from Eared Dove`);
    const url = `${environment.api_url_eared_dove}/data-source/chat/${chatId}/summary-metrics`;
    return this.http.post<ChatSummary>(url, models);
  }

  getDataSourceCategoryBuilderInfo(dataSourceID: string) {
    const url = `${environment.api_url_eared_dove}/data-source/data-source-info`;
    const params = new HttpParams().set("data_source_id", dataSourceID);
    return this.http.get<FilterFacetCounts>(url, { params });
  }

  getDataSourceInsightsData(
    dataSourceID: string,
    models: SelectedVersions[] = [],
  ) {
    const url = `${environment.api_url_eared_dove}/data-source/text-analysis-overview`;
    const params = new HttpParams().set("data_source_id", dataSourceID);
    return this.http.post<insightsChartParams>(url, models, { params });
  }

  getAvailableDimensions(dataSourceID: string) {
    const url = `${environment.api_url_eared_dove}/data-source/${dataSourceID}/available-dimensions`;
    return this.http.get<AvailableDimensions>(url);
  }

  /**
   * Append the retired ML dimension version IDs (topic/emotion/intent) only
   * when they exist. Raven-derived dimensions were retired, so post-retirement
   * data sources have no such versions and the params must be omitted rather
   * than sent as empty/invalid values. Legacy data sources still provide them
   * and keep full fidelity.
   */
  private appendRetiredMlVersions(
    params: HttpParams,
    topicVersionId?: string,
    emotionVersionId?: string,
    intentVersionId?: string,
  ): HttpParams {
    if (topicVersionId) {
      params = params.set("topic_version_id", topicVersionId);
    }
    if (emotionVersionId) {
      params = params.set("emotion_version_id", emotionVersionId);
    }
    if (intentVersionId) {
      params = params.set("intent_version_id", intentVersionId);
    }
    return params;
  }

  getOperands(
    dataSourceID: string,
    operandType: string | OperandType,
    categoryVersionId: string,
    sentimentVersion: string,
    topicVersionId: string,
    emotionVersionId: string,
    intentVersionId: string,
    sortProperty: string,
    sortDirection: string,
    sentimentFilterMax: number,
    sentimentFilterMaxInclusive: boolean,
    sentimentFilterMin: number,
    sentimentFilterMinInclusive: boolean,
    searchFilter: string,
    operandCount: number,
    from: number,
    structuredField: string = "",
    mlVersion?: string,
    expression?: any,
    uncatExpression?: any,
  ) {
    const url = `${environment.api_url_eared_dove}/category-builder/operands`;
    let params = new HttpParams()
      .set("data_source_id", dataSourceID)
      .set("operand_type", operandType)
      .set("sentiment_version_id", sentimentVersion);
    params = this.appendRetiredMlVersions(
      params,
      topicVersionId,
      emotionVersionId,
      intentVersionId,
    );
    params = params
      .set("sort_property", sortProperty)
      .set("sort_direction", sortDirection)
      .set("sentiment_filter_max", sentimentFilterMax.toString())
      .set(
        "sentiment_filter_max_inclusive",
        sentimentFilterMaxInclusive.toString(),
      )
      .set("sentiment_filter_min", sentimentFilterMin.toString())
      .set(
        "sentiment_filter_min_inclusive",
        sentimentFilterMinInclusive.toString(),
      )
      .set("search_filter", searchFilter)
      .set("operand_count", operandCount.toString())
      .set("from_", from.toString())
      .set("structured_field", structuredField)
      .set("category_version_id", categoryVersionId);
    if (mlVersion) {
      params = params.set("ml_version", mlVersion);
    }

    return this.http.post<PaginatedHits>(
      url,
      { expression, uncat_expression: uncatExpression },
      { params },
    );
  }

  getRecommendations(
    dataSourceID: string,
    topicVersionID: string,
    emotionVersionID: string,
    intentVersionID: string,
    userInput: string,
  ) {
    const url = `${environment.api_url_eared_dove}/category-builder/recommendations`;
    let params = new HttpParams().set("data_source_id", dataSourceID);
    params = this.appendRetiredMlVersions(
      params,
      topicVersionID,
      emotionVersionID,
      intentVersionID,
    );
    params = params.set("search_string", userInput);
    return this.http.get<Recommendations>(url, { params });
  }

  getCategorySentences(
    dataSourceId: string,
    sentimentVersionId: string,
    topicVersionId: string,
    emotionVersionId: string,
    intentVersionId: string,
    expression: Expression,
    from: number,
    size: number,
    sentimentSortDirection: SortDirection = null,
    categoryVersionId: string | null = null,
  ) {
    const url = `${environment.api_url_eared_dove}/category-builder/sentences`;
    let params = new HttpParams()
      .set("data_source_id", dataSourceId)
      .set("sentiment_version_id", sentimentVersionId);
    params = this.appendRetiredMlVersions(
      params,
      topicVersionId,
      emotionVersionId,
      intentVersionId,
    );
    params = params.set("from_", `${from}`).set("size", `${size}`);

    if (sentimentSortDirection) {
      params = params.append(
        "sentiment_sort_direction",
        sentimentSortDirection,
      );
    }

    // When the category version is known, the backend can serve already-applied
    // categories from the precomputed `categories` field (fast path) instead of
    // rebuilding the heavy expression query.
    if (categoryVersionId) {
      params = params.append("category_version_id", categoryVersionId);
    }

    return this.http.post<SentencesResults>(url, expression, { params });
  }

  searchSentences(
    inPhrase: string[],
    outPhrase: string[],
    modelType: string,
    dataSourceId: string,
  ) {
    const url = `${environment.api_url_eared_dove}/data-source/sentences/text-search`;
    const payload = {
      ins: inPhrase,
      outs: outPhrase,
    };
    const params = new HttpParams().set("data_source_id", dataSourceId);
    return this.http.post(url, payload, { params });
  }

  getchatSegments(
    chatId: string,
    models: SelectedVersions[] = [],
    session?: string,
  ) {
    this.logger.info(`Get a chat session for chat from Eared Dove`);
    const url = `${environment.api_url_eared_dove}/data-source/chat/segments`;
    let params;
    if (!session) {
      params = new HttpParams().set("chat", chatId);
    } else {
      params = new HttpParams().set("chat", chatId).set("session", session);
    }

    return this.http.post<ChatSegment[]>(url, models, { params });
  }

  startCategorizeAndPersist(
    dataSource: DataSource,
    sentimentVersionID: string,
    topicVersionID: string,
    emotionVersionID: string,
    intentVersionID: string,
    version: ModelVersion,
  ) {
    this.logger.info(
      `Starting categorize and persist job for data source ${dataSource.label} and version ${version.message}`,
    );
    const url = `${environment.api_url_eared_dove}/category-builder/start-categorize-and-persist`;
    let params = new HttpParams()
      .set("data_source_id", dataSource.uuid)
      .set("sentiment_version_id", sentimentVersionID);
    params = this.appendRetiredMlVersions(
      params,
      topicVersionID,
      emotionVersionID,
      intentVersionID,
    );
    params = params
      .set("category_version_id", version.uuid)
      .set("model_id", version.model_id);

    return this.http.post<Job>(url, null, { params });
  }

  migratePercolatorCategoryVersion(
    workspaceID: string,
    categoryVersionID: string,
    sentimentVersionID: string,
  ) {
    const url = `${environment.api_url_eared_dove}/percolator/migrate`;
    const params = new HttpParams()
      .set("workspace_id", workspaceID)
      .set("data_source_id", categoryVersionID)
      .set("category_version_id", categoryVersionID)
      .set("sentiment_version_id", sentimentVersionID);

    return this.http.post<PercolatorMigrationResponse>(url, null, { params });
  }

  getPercolatorStatsByCategoryVersion(categoryVersionID: string) {
    const url = `${environment.api_url_eared_dove}/percolator/stats/by-category-version/${categoryVersionID}`;
    return this.http.get<PercolatorStatsResponse>(url);
  }

  categorizeWithPercolator(
    workspaceID: string,
    categoryVersionID: string,
    texts: string[],
    maxMatchesPerText: number,
  ) {
    const url = `${environment.api_url_eared_dove.replace(/\/v0$/, "/v2")}/categorize`;
    const body: PercolatorCategorizeRequest = {
      workspace_id: workspaceID,
      category_version_id: categoryVersionID,
      texts,
      max_matches_per_text: maxMatchesPerText,
    };

    return this.http.post<PercolatorCategorizeResponse>(url, body);
  }

  startPercolatorApply(
    workspaceID: string,
    dataSourceID: string,
    categoryVersionID: string,
    sentimentVersionID: string,
    modelID: string,
    topicVersionID?: string,
    emotionVersionID?: string,
    intentVersionID?: string,
  ): Observable<Job> {
    this.logger.info(
      `Starting percolator apply-and-persist job for data source ${dataSourceID}`,
    );
    const url = `${environment.api_url_eared_dove}/percolator/start-apply-and-persist`;
    let params = new HttpParams()
      .set("workspace", workspaceID)
      .set("data_source_id", dataSourceID)
      .set("category_version_id", categoryVersionID)
      .set("sentiment_version_id", sentimentVersionID)
      .set("model_id", modelID);
    // Same dimensions the legacy apply sends. They are part of the key the
    // cached per-category metrics are stored under, so omitting them writes the
    // percolator's results to a row the category tree never reads.
    params = this.appendRetiredMlVersions(
      params,
      topicVersionID,
      emotionVersionID,
      intentVersionID,
    );

    return this.http.post<Job>(url, null, { params });
  }

  getPercolatorApplyReport(
    workspaceID: string,
    categoryVersionID: string,
    dataSourceID: string,
  ): Observable<CategorizeApplyReportComparison> {
    const url = `${environment.api_url_eared_dove}/percolator/apply-report/by-category-version/${categoryVersionID}`;
    const params = new HttpParams()
      .set("workspace", workspaceID)
      .set("data_source_id", dataSourceID);

    return this.http.get<CategorizeApplyReportComparison>(url, { params });
  }

  getPercolatorDeltaExamples(
    workspaceID: string,
    dataSourceID: string,
    categoryVersionID: string,
    sentimentVersionID: string,
    categoryId: string,
    size = 20,
  ): Observable<PercolatorDeltaExamples> {
    const url = `${environment.api_url_eared_dove}/percolator/delta-examples/by-category-version/${categoryVersionID}`;
    const params = new HttpParams()
      .set("workspace", workspaceID)
      .set("data_source_id", dataSourceID)
      .set("sentiment_version_id", sentimentVersionID)
      .set("category_id", categoryId)
      .set("size", `${size}`);
    return this.http.get<PercolatorDeltaExamples>(url, { params });
  }

  downloadPercolatorDeltaCsv(
    workspaceID: string,
    dataSourceID: string,
    categoryVersionID: string,
    sentimentVersionID: string,
  ): Observable<Blob> {
    const url = `${environment.api_url_eared_dove}/percolator/delta-export/by-category-version/${categoryVersionID}`;
    const params = new HttpParams()
      .set("workspace", workspaceID)
      .set("data_source_id", dataSourceID)
      .set("sentiment_version_id", sentimentVersionID);
    return this.http.get(url, { params, responseType: "blob" });
  }

  /**
   * Run ad-hoc categorization for arbitrary text against a category model's
   * released version. Used by the manual "Rule Tracer" panel.
   *
   * NOTE: the eared-dove endpoint always targets the model's *released*
   * version; unreleased edits to the working version are not reflected.
   * TODO: when an ingress for eared-dove-cx exists, fan the same payload
   * out to both deployments and compare result sets here.
   */
  categorizeTransient(
    categoryModelId: string,
    data: TransientData[],
    options: { translate?: boolean; persist?: boolean; explain?: boolean } = {},
  ): Observable<CategorizeSumupResponse> {
    this.logger.info(
      `Running ad-hoc categorize-sumup for category model ${categoryModelId}`,
    );
    const url = `${environment.api_url_eared_dove}/category-builder/start-categorize-sumup`;
    const params = new HttpParams()
      .set("category_model_id", categoryModelId)
      .set("global_translate", options.translate ? "true" : "false")
      .set("persist", options.persist === false ? "false" : "true")
      .set("explain", options.explain ? "true" : "false");
    return this.http.post<CategorizeSumupResponse>(url, data, { params });
  }

  /**
   * Run ad-hoc categorization for arbitrary text against a category model's
   * released version using the ES8 percolator engine. Used by the manual
   * "Percolator Rule Tracer" panel so the percolator matches can be compared
   * against the legacy engine's Rule Tracer output.
   *
   * NOTE: the eared-dove endpoint always targets the model's *released*
   * version and requires the percolator rules to have been migrated for that
   * version; otherwise a controlled error is returned.
   */
  percolateTransient(
    categoryModelId: string,
    data: TransientData[],
    options: { translate?: boolean; persist?: boolean; explain?: boolean } = {},
  ): Observable<CategorizeSumupResponse> {
    this.logger.info(
      `Running ad-hoc percolate categorize-sumup for category model ${categoryModelId}`,
    );
    const url = `${environment.api_url_eared_dove}/category-builder/start-categorize-sumup-v2`;
    const params = new HttpParams()
      .set("category_model_id", categoryModelId)
      .set("global_translate", options.translate ? "true" : "false")
      .set("persist", options.persist === false ? "false" : "true")
      .set("explain", options.explain ? "true" : "false");
    return this.http.post<CategorizeSumupResponse>(url, data, { params });
  }

  /*
   ** Get API usage detail from eared dove
   */
  getApiUsageDetails(
    start_date: string,
    end_date: string,
    category_model_ids: string[],
  ) {
    const url = `${environment.api_url_eared_dove}/api-usage/usage-detail`;

    let params = new HttpParams()
      .set("start_date", start_date)
      .set("end_date", end_date);

    for (const cmi of category_model_ids) {
      params = params.append("category_model_ids", cmi);
    }

    return this.http.get<ApiResponse>(url, { params });
  }

  getApiUsageDetailsCsv(
    startDate: string,
    endDate: string,
    categoryModelIds: string[],
    categoryModelLabels: string[],
  ) {
    const url = `${environment.api_url_eared_dove}/api-usage/usage-detail-csv`;
    const payload = {
      start_date: startDate,
      end_date: endDate,
      category_model_ids: categoryModelIds,
      category_model_labels: categoryModelLabels,
    };

    return this.http.post(url, payload);
  }

  getModelVersionApplication(
    dataSourceID: string,
    versionID: string,
    sentimentVersionID: string,
    topicVersionID: string,
    emotionVersionID: string,
    intentVersionID: string,
  ) {
    const url = `${environment.api_url_eared_dove}/category-builder/model-version-application`;
    let params = new HttpParams()
      .set("data_source_id", dataSourceID)
      .set("model_version_id", versionID)
      .set("sentiment_version_id", sentimentVersionID);
    params = this.appendRetiredMlVersions(
      params,
      topicVersionID,
      emotionVersionID,
      intentVersionID,
    );

    return this.http.get<CategoryModelVersionApplication>(url, {
      params,
    });
  }

  getHashes(
    modelVersionData: ModelVersionData,
    options: ModelVersionOptions,
    sentimentVersionID: string,
    topicVersionID: string,
    emotionVersionID: string,
    intentVersionID: string,
  ) {
    const url = `${environment.api_url_eared_dove}/category-builder/hashes`;
    let params = new HttpParams().set(
      "sentiment_version_id",
      sentimentVersionID,
    );
    params = this.appendRetiredMlVersions(
      params,
      topicVersionID,
      emotionVersionID,
      intentVersionID,
    );

    return this.http.post<CategoryHashes>(
      url,
      { model_version_data: modelVersionData, options },
      {
        params,
      },
    );
  }

  /*
   ** Get API usage report from eared dove
   */
  getApiUsageReport(
    start_date: string,
    end_date: string,
    workspaces: Array<string>,
  ) {
    const url = `${environment.api_url_eared_dove}/api-usage/usage-report`;

    let params;
    params = new HttpParams()
      .set("start_date", start_date)
      .set("end_date", end_date);
    const payload = {
      workspaces: workspaces,
    };

    return this.http.post<ApiUsageReportResponse>(url, payload, { params });
  }

  recompareCategorize(body: RecompareRequest) {
    const url = `${environment.api_url_eared_dove}/category-builder/recompare`;
    return this.http.post<RecompareReport>(url, body);
  }

  // Percolator production pilot schedules. The workspace interceptor appends
  // the active (source) workspace, which is where the windows are persisted.
  createPercolatorPilotSchedule(body: PercolatorPilotScheduleCreate) {
    const url = `${environment.api_url_eared_dove}/percolator-pilot/schedule`;
    return this.http.post<PercolatorPilotSchedule>(url, body);
  }

  listPercolatorPilotSchedules(sourceModelId?: string, includeInactive = true) {
    const url = `${environment.api_url_eared_dove}/percolator-pilot/schedule`;
    let params = new HttpParams().set(
      "include_inactive",
      String(includeInactive),
    );
    if (sourceModelId) {
      params = params.set("source_model_id", sourceModelId);
    }
    return this.http.get<PercolatorPilotSchedule[]>(url, { params });
  }

  deactivatePercolatorPilotSchedule(scheduleId: string) {
    const url = `${environment.api_url_eared_dove}/percolator-pilot/schedule/${scheduleId}`;
    return this.http.delete<void>(url);
  }

  listPercolatorPilotMirrorModels(mirrorWorkspaceId: string) {
    const url = `${environment.api_url_eared_dove}/percolator-pilot/schedule/mirror-models`;
    const params = new HttpParams().set(
      "mirror_workspace_id",
      mirrorWorkspaceId,
    );
    return this.http.get<PercolatorPilotMirrorModel[]>(url, { params });
  }

  listRecompareExecutions(query: RecompareExecutionParams) {
    const url = `${environment.api_url_eared_dove}/category-builder/recompare/executions`;
    let params = new HttpParams();
    if (query.dateMin) {
      params = params.set("date_min", query.dateMin);
    }
    if (query.dateMax) {
      params = params.set("date_max", query.dateMax);
    }
    if (query.categoryModelId) {
      params = params.set("category_model_id", query.categoryModelId);
    }
    if (query.limit) {
      params = params.set("limit", query.limit);
    }
    return this.http.get<RecompareExecution[]>(url, { params });
  }

  getApiUsageModelStats(params: ApiUsageAnalyticsParams) {
    const url = `${environment.api_url_eared_dove}/api-usage/model-stats`;
    return this.http.get<ApiUsageModelStatsResponse>(url, {
      params: this.buildApiUsageAnalyticsParams(params),
    });
  }

  getApiUsageModelStatsTimeseries(params: ApiUsageTimeseriesParams) {
    const url = `${environment.api_url_eared_dove}/api-usage/model-stats/timeseries`;
    return this.http.get<ApiUsageTimeseriesResponse>(url, {
      params: this.buildApiUsageAnalyticsParams(params),
    });
  }

  getApiUsageCategoryStats(params: ApiUsageAnalyticsParams) {
    const url = `${environment.api_url_eared_dove}/api-usage/category-stats`;
    return this.http.get<ApiUsageCategoryStatsResponse>(url, {
      params: this.buildApiUsageAnalyticsParams(params),
    });
  }

  private buildApiUsageAnalyticsParams(
    params: ApiUsageAnalyticsParams | ApiUsageTimeseriesParams,
  ): HttpParams {
    let httpParams = new HttpParams()
      .set("start_date", params.startDate)
      .set("end_date", params.endDate)
      .set("workspace", params.workspace);

    const optionalParams: Record<string, string | undefined> = {
      model_id: params.model_id,
      user_id: params.user_id,
      grain: (params as ApiUsageTimeseriesParams).grain,
      metric: (params as ApiUsageTimeseriesParams).metric,
    };

    Object.entries(optionalParams).forEach(([key, value]) => {
      if (value !== undefined && value !== "") {
        httpParams = httpParams.set(key, value);
      }
    });

    return httpParams;
  }

  getVerbatimExport(
    dataSourceId: string,
    sentimentVersionId: string,
    categoryModelId: string,
    categoryVersionId: string,
    categoryVersionData: ModelVersionData,
    categoryOptions: ModelVersionOptions,
    dataSourceType: DataSourceTypes,
    categoryModelLabel: string,
    topicVersionId: string,
    emotionVersionId: string,
    intentVersionId: string,
    reportType: string,
  ) {
    this.logger.info(`Exporting Verbatim ${reportType} Report`);
    const url = `${environment.api_url_eared_dove}/category-builder/verbatim-export`;
    const payload = {
      data_source_id: dataSourceId,
      sentiment_version_id: sentimentVersionId,
      category_model_id: categoryModelId,
      category_version_id: categoryVersionId,
      category_version_data: categoryVersionData,
      category_options: categoryOptions,
      data_source_type: dataSourceType,
      category_model_label: categoryModelLabel,
      topic_version_id: topicVersionId,
      emotion_version_id: emotionVersionId,
      intent_version_id: intentVersionId,
      report_type: reportType,
    };

    return this.http.post(url, payload);
  }

  getAuditExport(
    dataSourceId: string,
    sentimentVersionId: string,
    categoryModelId: string,
    categoryVersionId: string,
    categoryVersionData: ModelVersionData,
    dataSourceType: DataSourceTypes,
    categoryModelLabel: string,
    topicVersionId: string,
    emotionVersionId: string,
    intentVersionId: string,
  ) {
    this.logger.info("Exporting Audit Report");
    const url = `${environment.api_url_eared_dove}/category-builder/audit-export`;
    const payload = {
      data_source_id: dataSourceId,
      sentiment_version_id: sentimentVersionId,
      category_model_id: categoryModelId,
      category_version_id: categoryVersionId,
      category_version_data: categoryVersionData,
      data_source_type: dataSourceType,
      category_model_label: categoryModelLabel,
      topic_version_id: topicVersionId,
      emotion_version_id: emotionVersionId,
      intent_version_id: intentVersionId,
    };

    return this.http.post(url, payload);
  }

  getChatCategoryExport(
    dataSourceId: string,
    categoryVersionId: string,
    sentimentVersionId: string,
    topicVersionId: string,
    emotionVersionId: string,
    intentVersionId: string,
  ) {
    const url = `${environment.api_url_eared_dove}/category-builder/chat-category-export`;

    let params = new HttpParams()
      .set("data_source_id", dataSourceId)
      .set("category_version_id", categoryVersionId)
      .set("sentiment_version_id", sentimentVersionId);
    params = this.appendRetiredMlVersions(
      params,
      topicVersionId,
      emotionVersionId,
      intentVersionId,
    );

    // TODO: Make an interface for the response type
    return this.http.get<any>(url, { params });
  }

  getDeltaStats(
    dataSourceId: string,
    sentimentVersionId: string,
    topicVersionId: string,
    emotionVersionId: string,
    intentVersionId: string,
    originalExpression: Expression,
    deltaExpression: Expression,
  ) {
    const url = `${environment.api_url_eared_dove}/category-builder/delta-stats`;
    let params = new HttpParams()
      .set("data_source_id", dataSourceId)
      .set("sentiment_version_id", sentimentVersionId);
    params = this.appendRetiredMlVersions(
      params,
      topicVersionId,
      emotionVersionId,
      intentVersionId,
    );
    return this.http.post<DeltaStats>(
      url,
      {
        original_expression: originalExpression,
        delta_expression: deltaExpression,
      },
      { params },
    );
  }

  getLastApiRequests() {
    const url = `${environment.api_url_eared_dove}/api-usage/last-api-requests`;

    return this.http.get<LastApiRequests>(url);
  }

  getCategoryExportsFromLake() {
    const url = `${environment.api_url_eared_dove}/file-upload/list-files`;

    return this.http.get<ExportFile[]>(url);
  }

  getBlobUrlWithSas(fileID: string): Observable<string> {
    const params = new HttpParams().set("file_id", fileID);
    const url = `${environment.api_url_eared_dove}/file-upload/blob-url-with-sas`;
    return this.http.get<string>(url, { params });
  }

  deleteExportFileFromLake(fileID: string): any {
    const params = new HttpParams().set("file_id", fileID);
    const url = `${environment.api_url_eared_dove}/file-upload/download`;
    return this.http.delete(url, { params });
  }

  migrateElasticIndex(dataSourceID: string) {
    const url = `${environment.api_url_eared_dove}/data-source/migrate-elastic-index`;

    const params = new HttpParams().set("data_source_id", dataSourceID);
    return this.http.put(url, null, { params });
  }

  startModelApplication(
    dataSourceId: string,
    modelVersion: ModelVersion,
    modelType: CommonModelType,
  ) {
    const url = `${environment.api_url_eared_dove}/data-source/start-model-application`;

    const params = new HttpParams()
      .set("data_source_id", dataSourceId)
      .set("model_version_id", modelVersion.uuid)
      .set("model_type", modelType);
    return this.http.put(url, null, { params });
  }

  getSentimentDetails(
    modelVersionId: string,
    sentence: string,
  ): Observable<SentimentDetails[]> {
    const url = `${environment.api_url_eared_dove}/sentiment/details`;

    const params = new HttpParams().set("version_id", modelVersionId);
    const body = {
      data_set: [
        {
          document_id: "1",
          sentence,
        },
      ],
    };

    return this.http.post<SentimentDetails[]>(url, body, { params });
  }

  getVerbatimCategories(
    dataSourceId: string,
    verbatimId: string,
    dataSourceType: DataSourceTypes,
    sentimentVersionId: string,
    topicVersionId: string,
    emotionVersionId: string,
    intentVersionId: string,
    modelVersionId: string,
    modelVersionData: ModelVersionData,
    options: ModelVersionOptions,
  ) {
    const url = `${environment.api_url_eared_dove}/category-builder/verbatim-categories`;

    const params = new HttpParams()
      .set("data_source_id", dataSourceId)
      .set("verbatim_id", verbatimId)
      .set("data_source_type", dataSourceType)
      .set("sentiment_version_id", sentimentVersionId)
      .set("model_version_id", modelVersionId);

    return this.http.post<any>(
      url,
      {
        model_version_data: modelVersionData,
        options,
      },
      { params },
    );
  }

  cancelLargeUpload(folderName: string) {
    const url = `${environment.api_url_eared_dove}/file-upload/folder`;
    const params = new HttpParams().set("folder_name", folderName);
    return this.http.delete(url, { params });
  }

  getPersistentNotifications() {
    this.logger.info(`Get notifications for workspace from Eared Dove`);
    const url = `${environment.api_url_eared_dove}/persistent-notification`;
    return this.http.get<PersistentNotification[]>(url);
  }

  getTokenFrequencyWithPos(
    workspace: string,
    dataSourceId: string,
    size: number = 100,
  ) {
    this.logger.info(
      `Getting token frequency with POS for data source: ${dataSourceId}`,
    );
    const params = new HttpParams()
      .set("workspace", workspace)
      .set("data_source_id", dataSourceId);

    const url = `${environment.api_url_eared_dove}/data-source/token-frequency-pos`;
    return this.http.get<TokenFrequencyWithPos[]>(url, { params });
  }

  getDetailedTokenFrequencyWithPos(workspace: string, dataSourceId: string) {
    this.logger.info(
      `Getting detailed token frequency with POS for data source: ${dataSourceId}`,
    );
    const params = new HttpParams()
      .set("workspace", workspace)
      .set("data_source_id", dataSourceId);

    const url = `${environment.api_url_eared_dove}/data-source/token-frequency-pos/detailed`;
    return this.http.get<DetailedTokenFrequencyWithPos[]>(url, { params });
  }
}
